import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Gem, HelpCircle, History, CircleDot } from 'lucide-react'
import { toast } from 'react-hot-toast'
import useDoubleStore from '../store/useDoubleStore'
import useAppStore from '../../../store/useAppStore'
import useInventoryStore from '../../../store/useInventoryStore'
import DoubleWheel from '../components/DoubleWheel'
import DoubleBetModal from '../components/DoubleBetModal'
import DoubleBetsFeed from '../components/DoubleBetsFeed'
import DoubleWinnerSummary from '../components/DoubleWinnerSummary'
import HelpModal from '../../../components/HelpModal'

const formatSeconds = (secondsValue) => {
  const seconds = Math.max(0, Number(secondsValue || 0))
  const min = String(Math.floor(seconds / 60)).padStart(2, '0')
  const sec = String(seconds % 60).padStart(2, '0')
  return `${min}:${sec}`
}

const getStatusPollDelay = (status) => {
  if (status === 'raffle') return 500
  if (status === 'finished') return 500
  if (status === 'result') return 1000
  return 1000
}

const Double = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const {
    activeRound,
    inventory,
    balance,
    isBetting,
    loadActiveRound,
    loadBetForm,
    placeBet
  } = useDoubleStore()
  const { user, loadProfile } = useAppStore()
  const { items: inventoryItems, loadInventory } = useInventoryStore((state) => ({
    items: state.items,
    loadInventory: state.loadInventory
  }))

  const [isBetModalOpen, setIsBetModalOpen] = useState(false)
  const [isHelpOpen, setIsHelpOpen] = useState(false)
  const [selectedColor, setSelectedColor] = useState(null)
  const [countdown, setCountdown] = useState(Number(activeRound?.second_to_raffle || 0))

  const pollTimeoutRef = useRef(null)
  const timerIntervalRef = useRef(null)
  const abortControllerRef = useRef(null)
  const lastCountdownSyncRef = useRef({ roundId: null, status: null })

  const roundId = activeRound?.id
  const roundStatus = activeRound?.status || 'new'
  const bets = Array.isArray(activeRound?.bets) ? activeRound.bets : []
  const sums = {
    red: Number(activeRound?.sum_red || 0),
    black: Number(activeRound?.sum_black || 0),
    green: Number(activeRound?.sum_green || 0)
  }
  const stats100 = activeRound?.stats_100 || {}
  const modalInventory = useMemo(() => {
    const hasDoubleInventory = Array.isArray(inventory) && inventory.length > 0

    if (hasDoubleInventory) {
      return inventory
    }

    return (inventoryItems || [])
      .filter((item) => item.status === 'available' || item.status === 'returned')
      .map((item) => ({
        id: item.id,
        name: item.win?.name || item.name || `Item #${item.id}`,
        img: item.win?.img || item.img || '',
        cost: Number(item.win?.cost ?? item.cost ?? item.price ?? 0)
      }))
  }, [inventory, inventoryItems])

  const isBetLocked = ['raffle', 'result', 'finished'].includes(roundStatus)
  const currentBalance = Number(balance || user?.balance || 0)

  const handleStopPolling = () => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current)
      pollTimeoutRef.current = null
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
  }

  const startPolling = useCallback(async () => {
    const controller = new AbortController()
    abortControllerRef.current = controller

    try {
      const round = await loadActiveRound(controller.signal)
      const nextDelay = getStatusPollDelay(round?.status)
      pollTimeoutRef.current = setTimeout(startPolling, nextDelay)
    } catch (error) {
      pollTimeoutRef.current = setTimeout(startPolling, 1500)
    }
  }, [loadActiveRound])

  useEffect(() => {
    startPolling()
    return () => {
      handleStopPolling()
    }
  }, [startPolling])

  useEffect(() => {
    if (!activeRound?.id) return
    setIsBetModalOpen(false)
  }, [activeRound?.id])

  useEffect(() => {
    const nextRoundId = activeRound?.id || null
    const nextStatus = activeRound?.status || null
    const nextSeconds = Number(activeRound?.second_to_raffle || 0)
    const prevSync = lastCountdownSyncRef.current

    const isNewRound = prevSync.roundId !== nextRoundId
    const enteredBetStatus = !['new', 'started'].includes(prevSync.status) && ['new', 'started'].includes(nextStatus)

    if (isNewRound || enteredBetStatus) {
      setCountdown(nextSeconds)
    }

    lastCountdownSyncRef.current = {
      roundId: nextRoundId,
      status: nextStatus
    }
  }, [activeRound?.id, activeRound?.status, activeRound?.second_to_raffle])

  useEffect(() => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current)
      timerIntervalRef.current = null
    }

    if (!['new', 'started'].includes(roundStatus)) return

    timerIntervalRef.current = setInterval(() => {
      setCountdown((prev) => (prev > 0 ? prev - 1 : 0))
    }, 1000)

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current)
        timerIntervalRef.current = null
      }
    }
  }, [roundStatus])

  const handleOpenBetModal = (color) => {
    if (isBetLocked) return
    setSelectedColor(color)
    setIsBetModalOpen(true)
  }

  const handleLoadBetForm = useCallback(async () => {
    try {
      try {
        await loadProfile()
      } catch (error) {
        console.error('Ошибка обновления профиля перед ставкой Double:', error)
      }

      try {
        await loadInventory()
      } catch (error) {
        console.error('Ошибка загрузки общего инвентаря перед ставкой Double:', error)
      }

      await loadBetForm()
    } catch (error) {
      toast.error(t('double.toasts.betFormError', 'Не удалось загрузить форму ставки'))
    }
  }, [loadBetForm, loadInventory, loadProfile, t])

  const handleSubmitBet = async ({ color, crystal, items }) => {
    if (!roundId) {
      toast.error(t('double.toasts.roundUnavailable', 'Раунд недоступен'))
      return
    }

    try {
      await placeBet({ roundId, color, crystal, items })
      toast.success(t('double.toasts.betQueued', 'Ставка принята в очередь'))
      setIsBetModalOpen(false)
    } catch (error) {
      if (error.message === 'not enough balance') {
        toast.error(t('double.toasts.notEnoughBalance', 'Недостаточно кристаллов'))
        return
      }
      if (error.message === 'error sum') {
        toast.error(t('double.toasts.minBet', 'Минимальная ставка 100 кристаллов'))
        return
      }
      if (error.message === 'error max bet') {
        toast.error(t('double.toasts.maxBet', 'Превышен лимит 100 000 кристаллов'))
        return
      }
      toast.error(t('double.toasts.betError', 'Не удалось отправить ставку'))
    }
  }

  const statsRows = useMemo(() => {
    const total = Number(stats100.red || 0) + Number(stats100.black || 0) + Number(stats100.green || 0)
    const safeTotal = total > 0 ? total : 1

    return [
      { key: 'red', label: t('double.colors.red', 'Красный'), value: Number(stats100.red || 0), colorClass: 'bg-red-500' },
      { key: 'black', label: t('double.colors.black', 'Чёрный'), value: Number(stats100.black || 0), colorClass: 'bg-slate-300' },
      { key: 'green', label: t('double.colors.green', 'Зелёный'), value: Number(stats100.green || 0), colorClass: 'bg-emerald-400' }
    ].map((item) => ({
      ...item,
      percent: (item.value / safeTotal) * 100
    }))
  }, [stats100, t])

  const helpSteps = useMemo(() => ([
    t('double.help.step1', '1) Выберите цвет ставки: красный или чёрный дают x2, зелёный даёт x14.'),
    t('double.help.step2', '2) Добавьте кристаллы и/или предметы, минимальная ставка — 100 кристаллов.'),
    t('double.help.step3', '3) Дождитесь розыгрыша: колесо определит сектор и выигравший цвет раунда.')
  ]), [t])

  return (
    <div className="px-4 py-6 pb-16 lg:px-6 lg:py-2 lg:pb-8">
      <div className="mx-auto max-w-7xl space-y-6 lg:space-y-8">
        <div className="flex items-center justify-center">
          <div className="flex items-center gap-2">
            <h1 className="flex items-center gap-2 text-2xl lg:text-4xl font-bold text-accent-primary">
              <CircleDot className="w-6 h-6 shrink-0 lg:w-8 lg:h-8" />
              <span>{t('double.title', 'Double')}</span>
            </h1>
            <button
              type="button"
              onClick={() => setIsHelpOpen(true)}
              className="p-1.5 rounded-full bg-dark-secondary/50 hover:bg-dark-secondary transition-colors"
              aria-label={t('double.help.button', 'Справка по режиму Double')}
            >
              <HelpCircle className="w-5 h-5 text-text-secondary" />
            </button>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_350px]">
          <div className="space-y-6">
            <DoubleWheel
              roundId={roundId}
              status={roundStatus}
              prevSector={activeRound?.prev_round?.win_sector}
              winSector={activeRound?.win_sector}
              winColor={activeRound?.win_color}
              countdownSeconds={countdown}
              raffleSeconds={activeRound?.second_to_finish_raffle}
            />

            {['result', 'finished'].includes(roundStatus) && (
              <DoubleWinnerSummary
                bets={bets}
                winColor={activeRound?.win_color}
                winSector={activeRound?.win_sector}
              />
            )}

            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <button
                disabled={isBetLocked}
                onClick={() => handleOpenBetModal('red')}
                className="relative overflow-hidden rounded-2xl border border-red-300/45 bg-gradient-to-br from-red-500/50 via-rose-500/30 to-red-900/45 px-4 py-3 text-left shadow-[0_8px_24px_rgba(239,68,68,0.28)] transition-all duration-300 hover:-translate-y-0.5 hover:border-red-200/70 hover:shadow-[0_14px_34px_rgba(239,68,68,0.45)] active:translate-y-0 disabled:opacity-45 disabled:transform-none disabled:shadow-none"
              >
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_15%,rgba(255,255,255,0.24),transparent_45%)]" />
                <p className="relative text-red-50 font-semibold tracking-wide">{t('double.colors.red', 'Красный')} ×2</p>
                <p className="relative text-red-100 text-sm mt-1 inline-flex items-center gap-1 font-medium">
                  {sums.red.toLocaleString()} <Gem className="inline w-3.5 h-3.5" />
                </p>
              </button>

              <button
                disabled={isBetLocked}
                onClick={() => handleOpenBetModal('black')}
                className="relative overflow-hidden rounded-2xl border border-slate-300/35 bg-gradient-to-br from-slate-700/70 via-slate-900/80 to-black/90 px-4 py-3 text-left shadow-[0_8px_24px_rgba(2,6,23,0.55)] transition-all duration-300 hover:-translate-y-0.5 hover:border-slate-200/70 hover:shadow-[0_14px_34px_rgba(2,6,23,0.75)] active:translate-y-0 disabled:opacity-45 disabled:transform-none disabled:shadow-none"
              >
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_15%,rgba(255,255,255,0.14),transparent_45%)]" />
                <p className="relative text-slate-100 font-semibold tracking-wide">{t('double.colors.black', 'Чёрный')} ×2</p>
                <p className="relative text-slate-200 text-sm mt-1 inline-flex items-center gap-1 font-medium">
                  {sums.black.toLocaleString()} <Gem className="inline w-3.5 h-3.5" />
                </p>
              </button>

              <button
                disabled={isBetLocked}
                onClick={() => handleOpenBetModal('green')}
                className="relative overflow-hidden rounded-2xl border border-emerald-300/45 bg-gradient-to-br from-emerald-400/55 via-emerald-500/35 to-emerald-900/50 px-4 py-3 text-left shadow-[0_8px_24px_rgba(16,185,129,0.28)] transition-all duration-300 hover:-translate-y-0.5 hover:border-emerald-200/70 hover:shadow-[0_14px_34px_rgba(16,185,129,0.45)] active:translate-y-0 disabled:opacity-45 disabled:transform-none disabled:shadow-none"
              >
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_15%,rgba(255,255,255,0.24),transparent_45%)]" />
                <p className="relative text-emerald-50 font-semibold tracking-wide">{t('double.colors.green', 'Зелёный')} ×14</p>
                <p className="relative text-emerald-100 text-sm mt-1 inline-flex items-center gap-1 font-medium">
                  {sums.green.toLocaleString()} <Gem className="inline w-3.5 h-3.5" />
                </p>
              </button>
            </div>

            <DoubleBetsFeed bets={bets} />
          </div>

          <div className="space-y-6 lg:sticky lg:top-28 h-fit">
            <div className="rounded-2xl border border-white/10 bg-surface/50 p-4 hidden">
              <p className="text-xs uppercase tracking-[0.18em] text-text-secondary">{t('double.status.title', 'Статус раунда')}</p>
              <p className="mt-2 text-white text-lg font-semibold">{roundStatus}</p>

              <div className="mt-4 rounded-xl border border-white/10 bg-dark-primary/50 p-3">
                <p className="text-xs text-text-secondary">{t('double.status.timer', 'До розыгрыша')}</p>
                <p className="mt-1 font-mono text-2xl text-accent-primary">{formatSeconds(countdown)}</p>
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-surface/50 p-4">
              <h3 className="text-white font-semibold mb-3">{t('double.stats100.title', '100 последних раундов')}</h3>
              <div className="space-y-3">
                {statsRows.map((row) => (
                  <div key={row.key}>
                    <div className="mb-1 flex items-center justify-between text-xs">
                      <span className="text-text-secondary">{row.label}</span>
                      <span className="text-white">{row.value}</span>
                    </div>
                    <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                      <div className={`${row.colorClass} h-full`} style={{ width: `${row.percent}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <button
                type="button"
                onClick={() => navigate('/double/history')}
                className="mt-4 inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-full bg-surface text-text-secondary hover:text-white border border-white/10 transition-colors"
              >
                <History className="w-4 h-4" />
                <span className="text-sm">{t('double.buttons.history', 'История')}</span>
            </button>

          </div>
        </div>
      </div>

      <HelpModal
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        title={t('double.help.title', 'Как работает Double')}
        steps={helpSteps}
        highlightLastStep={false}
      />

      <DoubleBetModal
        isOpen={isBetModalOpen}
        color={selectedColor}
        isLoading={isBetting}
        balance={currentBalance}
        inventory={modalInventory}
        onClose={() => setIsBetModalOpen(false)}
        onLoadBetForm={handleLoadBetForm}
        onSubmit={handleSubmitBet}
      />
    </div>
  )
}

export default Double
